import React, { useState, useMemo } from 'react';
import {
  DownloadCloud,
  FileText,
  Printer,
  RotateCcw,
  ChevronDown,
  TrendingUp,
  Users,
  Calendar,
  Filter,
} from 'lucide-react';

// ============================================================================
// CHART HELPER COMPONENTS
// ============================================================================

function BarChart({ data, width = 500, height = 300, color = '#2E8B98', horizontal = false }) {
  if (!data || data.length === 0) {
    return <div style={{ color: '#6C757D' }}>No data available</div>;
  }

  const maxValue = Math.max(...data.map(d => d.value || 0));
  const padding = { top: 30, right: 20, bottom: 50, left: horizontal ? 120 : 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  if (horizontal) {
    const barHeight = chartHeight / data.length;
    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto' }}>
        {/* Title area */}
        <text x={width / 2} y={20} textAnchor="middle" style={{ fontSize: '14px', fontWeight: 'bold', fill: '#212529' }}>
          Department Breakdown
        </text>

        {/* Y-axis labels and bars */}
        {data.map((item, idx) => {
          const barWidth = (item.value / maxValue) * chartWidth;
          const y = padding.top + idx * barHeight;
          const percentage = ((item.value / data.reduce((s, d) => s + d.value, 0)) * 100).toFixed(1);
          return (
            <g key={idx}>
              <text
                x={padding.left - 10}
                y={y + barHeight / 2 + 5}
                textAnchor="end"
                style={{ fontSize: '12px', fill: '#212529' }}
              >
                {item.label}
              </text>
              <rect
                x={padding.left}
                y={y + 5}
                width={barWidth}
                height={barHeight - 10}
                fill={color}
                style={{ opacity: 0.85, cursor: 'pointer' }}
              />
              <text
                x={padding.left + barWidth + 8}
                y={y + barHeight / 2 + 5}
                style={{ fontSize: '11px', fill: '#212529', fontWeight: 'bold' }}
              >
                {item.value} ({percentage}%)
              </text>
            </g>
          );
        })}
      </svg>
    );
  }

  // Vertical bars
  const barWidth = chartWidth / data.length;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto' }}>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
        const yPos = padding.top + (1 - ratio) * chartHeight;
        const val = (ratio * maxValue).toFixed(0);
        return (
          <g key={idx}>
            <line x1={padding.left} y1={yPos} x2={width - padding.right} y2={yPos} stroke="#E9ECEF" strokeWidth="1" />
            <text x={padding.left - 5} y={yPos + 4} textAnchor="end" style={{ fontSize: '11px', fill: '#6C757D' }}>
              {val}
            </text>
          </g>
        );
      })}

      {/* Bars */}
      {data.map((item, idx) => {
        const barHeight = (item.value / maxValue) * chartHeight;
        const x = padding.left + idx * barWidth;
        const y = padding.top + chartHeight - barHeight;
        return (
          <g key={idx}>
            <rect x={x + 5} y={y} width={barWidth - 10} height={barHeight} fill={color} style={{ opacity: 0.85 }} />
            <text
              x={x + barWidth / 2}
              y={padding.top + chartHeight + 20}
              textAnchor="middle"
              style={{ fontSize: '11px', fill: '#212529' }}
            >
              {item.label}
            </text>
            <text
              x={x + barWidth / 2}
              y={y - 5}
              textAnchor="middle"
              style={{ fontSize: '10px', fill: '#212529', fontWeight: 'bold' }}
            >
              {item.value}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function LineChart({ data, width = 500, height = 300, color = '#2E8B98' }) {
  if (!data || data.length === 0) {
    return <div style={{ color: '#6C757D' }}>No data available</div>;
  }

  const maxValue = Math.max(...data.map(d => d.value || 0));
  const minValue = Math.min(...data.map(d => d.value || 0));
  const range = maxValue - minValue || 1;
  const padding = { top: 30, right: 20, bottom: 50, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const points = data.map((item, idx) => {
    const x = padding.left + (idx / (data.length - 1 || 1)) * chartWidth;
    const y = padding.top + chartHeight - ((item.value - minValue) / range) * chartHeight;
    return { x, y, ...item };
  });

  const pathD = points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto' }}>
      {/* Grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
        const yPos = padding.top + (1 - ratio) * chartHeight;
        const val = (minValue + ratio * range).toFixed(0);
        return (
          <g key={idx}>
            <line x1={padding.left} y1={yPos} x2={width - padding.right} y2={yPos} stroke="#E9ECEF" strokeWidth="1" />
            <text x={padding.left - 5} y={yPos + 4} textAnchor="end" style={{ fontSize: '11px', fill: '#6C757D' }}>
              {val}
            </text>
          </g>
        );
      })}

      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

      {/* Points */}
      {points.map((p, idx) => (
        <circle key={idx} cx={p.x} cy={p.y} r="4" fill={color} style={{ opacity: 0.8 }} />
      ))}

      {/* X-axis labels */}
      {points.map((p, idx) => (
        idx % Math.ceil(points.length / 6) === 0 && (
          <text
            key={idx}
            x={p.x}
            y={padding.top + chartHeight + 20}
            textAnchor="middle"
            style={{ fontSize: '11px', fill: '#212529' }}
          >
            {p.label}
          </text>
        )
      ))}
    </svg>
  );
}

function PieChart({ data, size = 250, color = '#2E8B98' }) {
  if (!data || data.length === 0) {
    return <div style={{ color: '#6C757D' }}>No data available</div>;
  }

  const total = data.reduce((s, d) => s + d.value, 0);
  const colors = [
    color,
    '#62BB46',
    '#F26522',
    '#DC3545',
    '#6C757D',
    '#17A2B8',
  ];

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 20;

  let currentAngle = -Math.PI / 2;
  const slices = [];

  data.forEach((item, idx) => {
    const sliceAngle = (item.value / total) * 2 * Math.PI;
    const startAngle = currentAngle;
    const endAngle = currentAngle + sliceAngle;

    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);

    const largeArc = sliceAngle > Math.PI ? 1 : 0;
    const pathData = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

    const labelAngle = startAngle + sliceAngle / 2;
    const labelRadius = radius * 0.65;
    const labelX = cx + labelRadius * Math.cos(labelAngle);
    const labelY = cy + labelRadius * Math.sin(labelAngle);
    const percentage = ((item.value / total) * 100).toFixed(1);

    slices.push({
      path: pathData,
      color: colors[idx % colors.length],
      labelX,
      labelY,
      label: `${percentage}%`,
      title: item.label,
    });

    currentAngle = endAngle;
  });

  return (
    <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', height: 'auto', maxWidth: '300px' }}>
      {slices.map((slice, idx) => (
        <g key={idx}>
          <path d={slice.path} fill={slice.color} style={{ opacity: 0.85, cursor: 'pointer' }} />
          <text
            x={slice.labelX}
            y={slice.labelY}
            textAnchor="middle"
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              fill: '#FFF',
              pointerEvents: 'none',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            }}
          >
            {slice.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

function ProgressBar({ value, max, color = '#2E8B98', label = '' }) {
  const percentage = Math.min((value / max) * 100, 100);
  return (
    <div style={{ marginBottom: '12px' }}>
      {label && <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px', color: '#212529' }}>{label}</div>}
      <div
        style={{
          height: '8px',
          background: '#E9ECEF',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: 'inset 2px 2px 4px rgba(186,199,216,0.3)',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${percentage}%`,
            background: color,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ReportsTab({ personnel = [] }) {
  const [activeReport, setActiveReport] = useState('headcount');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [departmentFilter, setDepartmentFilter] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState(new Set());

  // Colors
  const colors = {
    primary: '#2E8B98',
    success: '#62BB46',
    warning: '#F26522',
    danger: '#DC3545',
    bg: '#F8F9FA',
    text: '#212529',
    textSec: '#6C757D',
    border: '#DEE2E6',
  };

  const neuShadow = '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)';
  const neuInset = 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)';

  // ========================================================================
  // DATA CALCULATIONS
  // ========================================================================

  const filteredPersonnel = useMemo(() => {
    let filtered = personnel;

    // Apply department filter
    if (departmentFilter.size > 0) {
      filtered = filtered.filter(p => departmentFilter.has(p.department));
    }

    // Apply status filter
    if (statusFilter.size > 0) {
      filtered = filtered.filter(p => statusFilter.has(p.status));
    }

    // Apply date range if specified
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(p => {
        const hireDate = new Date(p.hireDate);
        if (dateRange.start && hireDate < new Date(dateRange.start)) return false;
        if (dateRange.end && hireDate > new Date(dateRange.end)) return false;
        return true;
      });
    }

    return filtered;
  }, [personnel, departmentFilter, statusFilter, dateRange]);

  // Headcount data
  const headcountByStatus = useMemo(() => {
    const counts = {};
    filteredPersonnel.forEach(p => {
      counts[p.status] = (counts[p.status] || 0) + 1;
    });
    return Object.entries(counts).map(([status, count]) => ({
      label: status || 'Unknown',
      value: count,
      color: status === 'Active' ? colors.success : status === 'Inactive' ? colors.textSec : colors.warning,
    }));
  }, [filteredPersonnel, colors]);

  // Department data
  const departmentData = useMemo(() => {
    const counts = {};
    personnel.forEach(p => {
      counts[p.department] = (counts[p.department] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([dept, count]) => ({ label: dept || 'Unassigned', value: count }))
      .sort((a, b) => b.value - a.value);
  }, [personnel]);

  // Tenure analysis
  const tenureAnalysis = useMemo(() => {
    const buckets = {
      '<1yr': 0,
      '1-2yr': 0,
      '2-3yr': 0,
      '3-5yr': 0,
      '5+yr': 0,
    };

    const today = new Date();
    filteredPersonnel.forEach(p => {
      const hireDate = new Date(p.hireDate);
      const yearsWorked = (today - hireDate) / (1000 * 60 * 60 * 24 * 365.25);

      if (yearsWorked < 1) buckets['<1yr']++;
      else if (yearsWorked < 2) buckets['1-2yr']++;
      else if (yearsWorked < 3) buckets['2-3yr']++;
      else if (yearsWorked < 5) buckets['3-5yr']++;
      else buckets['5+yr']++;
    });

    return Object.entries(buckets).map(([label, value]) => ({ label, value }));
  }, [filteredPersonnel]);

  const avgTenure = useMemo(() => {
    if (filteredPersonnel.length === 0) return 0;
    const today = new Date();
    const totalYears = filteredPersonnel.reduce((sum, p) => {
      const yearsWorked = (today - new Date(p.hireDate)) / (1000 * 60 * 60 * 24 * 365.25);
      return sum + yearsWorked;
    }, 0);
    return (totalYears / filteredPersonnel.length).toFixed(1);
  }, [filteredPersonnel]);

  const longestServing = useMemo(() => {
    const today = new Date();
    return filteredPersonnel
      .map(p => ({
        ...p,
        yearsWorked: ((today - new Date(p.hireDate)) / (1000 * 60 * 60 * 24 * 365.25)).toFixed(1),
      }))
      .sort((a, b) => b.yearsWorked - a.yearsWorked)
      .slice(0, 5);
  }, [filteredPersonnel]);

  const newestHires = useMemo(() => {
    return filteredPersonnel
      .sort((a, b) => new Date(b.hireDate) - new Date(a.hireDate))
      .slice(0, 5);
  }, [filteredPersonnel]);

  // Turnover data
  const turnoverMetrics = useMemo(() => {
    const terminated = personnel.filter(p => p.status === 'Terminated').length;
    const rate = personnel.length > 0 ? ((terminated / personnel.length) * 100).toFixed(2) : 0;
    return { terminated, rate };
  }, [personnel]);

  const monthlyTurnover = useMemo(() => {
    const months = {};
    const today = new Date();

    for (let i = 11; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      months[key] = 0;
    }

    personnel
      .filter(p => p.status === 'Terminated' && p.terminationDate)
      .forEach(p => {
        const termDate = new Date(p.terminationDate);
        const key = termDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        if (key in months) months[key]++;
      });

    return Object.entries(months).map(([label, value]) => ({ label, value }));
  }, [personnel]);

  const turnoverByDept = useMemo(() => {
    const deptTurnover = {};
    const deptTotal = {};

    personnel.forEach(p => {
      const dept = p.department || 'Unassigned';
      deptTotal[dept] = (deptTotal[dept] || 0) + 1;
      if (p.status === 'Terminated') {
        deptTurnover[dept] = (deptTurnover[dept] || 0) + 1;
      }
    });

    return Object.entries(deptTotal)
      .map(([dept, total]) => ({
        label: dept,
        value: deptTurnover[dept] || 0,
        rate: ((((deptTurnover[dept] || 0) / total) * 100).toFixed(1)),
      }))
      .sort((a, b) => b.rate - a.rate);
  }, [personnel]);

  // Job category distribution
  const jobCategoryDist = useMemo(() => {
    const counts = {};
    filteredPersonnel.forEach(p => {
      const cat = p.jobCategory || 'Other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts).map(([label, value]) => ({ label, value }));
  }, [filteredPersonnel]);

  // Diversity index (simplified)
  const diversityIndex = useMemo(() => {
    const deptDiversity = {};
    const deptGenders = {};

    filteredPersonnel.forEach(p => {
      const dept = p.department || 'Unassigned';
      deptGenders[dept] = deptGenders[dept] || {};
      const gender = p.gender || 'Other';
      deptGenders[dept][gender] = (deptGenders[dept][gender] || 0) + 1;
    });

    departmentData.forEach(({ label: dept }) => {
      const genders = deptGenders[dept] || {};
      const total = Object.values(genders).reduce((s, v) => s + v, 0);
      let hIndex = 0;
      Object.values(genders).forEach(count => {
        hIndex += (count / total) ** 2;
      });
      deptDiversity[dept] = (1 - hIndex).toFixed(3);
    });

    return deptDiversity;
  }, [filteredPersonnel, departmentData]);

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const renderReportContent = () => {
    switch (activeReport) {
      case 'headcount':
        return renderHeadcountAnalysis();
      case 'department':
        return renderDepartmentBreakdown();
      case 'tenure':
        return renderTenureAnalysis();
      case 'turnover':
        return renderTurnoverReport();
      case 'diversity':
        return renderDiversityOverview();
      case 'compensation':
        return (
          <div style={{ padding: '20px', color: colors.textSec, textAlign: 'center' }}>
            <p>Compensation Overview — Coming Soon</p>
          </div>
        );
      default:
        return null;
    }
  };

  const renderHeadcountAnalysis = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ color: colors.text, marginBottom: '12px' }}>Total Headcount</h3>
        <div
          style={{
            fontSize: '36px',
            fontWeight: 'bold',
            color: colors.primary,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          {filteredPersonnel.length}
          {filteredPersonnel.length > 100 && <TrendingUp size={28} color={colors.success} />}
        </div>
        <p style={{ color: colors.textSec, fontSize: '12px', marginTop: '4px' }}>
          {selectedDepartment ? `in ${selectedDepartment}` : 'across all departments'}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '16px', fontSize: '14px' }}>By Status</h4>
          <PieChart data={headcountByStatus} size={250} />
          <div style={{ marginTop: '16px', fontSize: '12px' }}>
            {headcountByStatus.map((status, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: colors.text }}>{status.label}</span>
                <span style={{ fontWeight: 'bold', color: status.color }}>{status.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '16px', fontSize: '14px' }}>Monthly Trend (12 Months)</h4>
          <LineChart data={monthlyTrend()} width={400} height={250} color={colors.primary} />
        </div>
      </div>

      <div
        style={{
          padding: '20px',
          background: colors.bg,
          borderRadius: '12px',
          boxShadow: neuShadow,
        }}
      >
        <h4 style={{ color: colors.text, marginBottom: '16px' }}>Growth Rate</h4>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: colors.success }}>
          {calculateGrowthRate().toFixed(2)}%
        </div>
        <p style={{ color: colors.textSec, fontSize: '12px', marginTop: '8px' }}>Year-over-year growth</p>
      </div>
    </div>
  );

  const renderDepartmentBreakdown = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '16px' }}>Department Headcount</h4>
          <BarChart data={departmentData} width={500} height={350} color={colors.primary} horizontal={true} />
        </div>

        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '16px' }}>Department KPIs</h4>
          <div style={{ fontSize: '13px', color: colors.text, lineHeight: '1.8' }}>
            <div style={{ marginBottom: '12px' }}>
              <strong>Largest Department:</strong> {departmentData[0]?.label} ({departmentData[0]?.value} employees)
            </div>
            <div style={{ marginBottom: '12px' }}>
              <strong>Smallest Department:</strong> {departmentData[departmentData.length - 1]?.label} ({departmentData[departmentData.length - 1]?.value} employees)
            </div>
            <div>
              <strong>Avg Department Size:</strong> {departmentData.length > 0 ? (departmentData.reduce((s, d) => s + d.value, 0) / departmentData.length).toFixed(1) : 0} employees
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: '20px',
          background: colors.bg,
          borderRadius: '12px',
          boxShadow: neuShadow,
        }}
      >
        <h4 style={{ color: colors.text, marginBottom: '16px' }}>Personnel in {selectedDepartment || 'Selected Department'}</h4>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {departmentData.map(dept => (
            <button
              key={dept.label}
              onClick={() => setSelectedDepartment(selectedDepartment === dept.label ? null : dept.label)}
              style={{
                padding: '8px 12px',
                background: selectedDepartment === dept.label ? colors.primary : colors.bg,
                color: selectedDepartment === dept.label ? '#FFF' : colors.text,
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                boxShadow: neuShadow,
              }}
            >
              {dept.label} ({dept.value})
            </button>
          ))}
        </div>

        {selectedDepartment && (
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                <th style={{ padding: '8px', textAlign: 'left', color: colors.text, fontWeight: 'bold' }}>Name</th>
                <th style={{ padding: '8px', textAlign: 'left', color: colors.text, fontWeight: 'bold' }}>Job Title</th>
                <th style={{ padding: '8px', textAlign: 'left', color: colors.text, fontWeight: 'bold' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredPersonnel
                .filter(p => p.department === selectedDepartment)
                .map((person, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '8px', color: colors.text }}>{person.firstName} {person.lastName}</td>
                    <td style={{ padding: '8px', color: colors.textSec }}>{person.jobTitle}</td>
                    <td style={{ padding: '8px' }}>
                      <span
                        style={{
                          padding: '2px 6px',
                          background: person.status === 'Active' ? colors.success : colors.warning,
                          color: '#FFF',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                        }}
                      >
                        {person.status}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderTenureAnalysis = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '16px' }}>Tenure Distribution</h4>
          <BarChart data={tenureAnalysis} width={500} height={300} color={colors.primary} />
        </div>

        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ color: colors.text, marginBottom: '12px' }}>Average Tenure</h4>
            <div style={{ fontSize: '42px', fontWeight: 'bold', color: colors.primary }}>
              {avgTenure} <span style={{ fontSize: '18px' }}>years</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '16px' }}>Longest-Serving Employees</h4>
          <div style={{ fontSize: '12px' }}>
            {longestServing.map((person, idx) => (
              <div key={idx} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ fontWeight: 'bold', color: colors.text }}>
                  {person.firstName} {person.lastName}
                </div>
                <div style={{ color: colors.textSec, fontSize: '11px' }}>
                  {person.yearsWorked} years • {person.jobTitle}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '16px' }}>Newest Hires</h4>
          <div style={{ fontSize: '12px' }}>
            {newestHires.map((person, idx) => (
              <div key={idx} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: `1px solid ${colors.border}` }}>
                <div style={{ fontWeight: 'bold', color: colors.text }}>
                  {person.firstName} {person.lastName}
                </div>
                <div style={{ color: colors.textSec, fontSize: '11px' }}>
                  Hired: {new Date(person.hireDate).toLocaleDateString()} • {person.jobTitle}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTurnoverReport = () => (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '12px', fontSize: '14px' }}>Turnover Rate</h4>
          <div style={{ fontSize: '36px', fontWeight: 'bold', color: colors.danger }}>
            {turnoverMetrics.rate}%
          </div>
          <p style={{ color: colors.textSec, fontSize: '11px', marginTop: '8px' }}>
            {turnoverMetrics.terminated} terminated
          </p>
        </div>

        <div
          style={{
            padding: '20px',
            background: colors.bg,
            borderRadius: '12px',
            boxShadow: neuShadow,
          }}
        >
          <h4 style={{ color: colors.text, marginBottom: '12px', fontSize: '14px' }}>Total Employees</h4>
          <div style={{ fontSize: '36px', fontWeight: 'bold', color: colors.primary }}>
            {personnel.length}
          </div>
        </div>
      </div>

      <div
        style={{
          padding: '20px',
          background: colors.bg,
          borderRadius: '12px',
          boxShadow: neuShadow,
          marginBottom: '20px',
        }}
      >
        <h4 style={{ color: colors.text, marginBottom: '16px' }}>Monthly Turnover</h4>
        <BarChart data={monthlyTurnover} width={600} height={300} color={colors.danger} />
      </div>

      <div
        style={{
          padding: '20px',
          background: colors.bg,
          borderRadius: '12px',
          boxShadow: neuShadow,
        }}
      >
        <h4 style={{ color: colors.text, marginBottom: '16px' }}>Turnover by Department</h4>
        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
              <th style={{ padding: '8px', textAlign: 'left', color: colors.text, fontWeight: 'bold' }}>Department</th>
              <th style={{ padding: '8px', textAlign: 'center', color: colors.text, fontWeight: 'bold' }}>Terminated</th>
              <th style={{ padding: '8px', textAlign: 'center', color: colors.text, fontWeight: 'bold' }}>Turnover Rate</th>
            </tr>
          </thead>
          <tbody>
            {turnoverByDept.map((dept, idx) => (
              <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                <td style={{ padding: '8px', color: colors.text }}>{dept.label}</td>
                <td style={{ padding: '8px', textAlign: 'center', color: colors.danger, fontWeight: 'bold' }}>
                  {dept.value}
                </td>
                <td style={{ padding: '8px', textAlign: 'center', color: colors.text }}>
                  <span style={{ background: colors.warning, color: '#FFF', padding: '2px 6px', borderRadius: '4px', fontSize: '10px' }}>
                    {dept.rate}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDiversityOverview = () => (
    <div style={{ padding: '20px' }}>
      <div
        style={{
          padding: '20px',
          background: colors.bg,
          borderRadius: '12px',
          boxShadow: neuShadow,
          marginBottom: '20px',
        }}
      >
        <h4 style={{ color: colors.text, marginBottom: '16px' }}>Job Category Distribution</h4>
        <PieChart data={jobCategoryDist} size={300} />
      </div>

      <div
        style={{
          padding: '20px',
          background: colors.bg,
          borderRadius: '12px',
          boxShadow: neuShadow,
        }}
      >
        <h4 style={{ color: colors.text, marginBottom: '16px' }}>Department Diversity Index</h4>
        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
              <th style={{ padding: '8px', textAlign: 'left', color: colors.text, fontWeight: 'bold' }}>Department</th>
              <th style={{ padding: '8px', textAlign: 'left', color: colors.text, fontWeight: 'bold' }}>Diversity Index</th>
            </tr>
          </thead>
          <tbody>
            {departmentData.map((dept, idx) => {
              const diversity = diversityIndex[dept.label] || 0;
              return (
                <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={{ padding: '8px', color: colors.text }}>{dept.label}</td>
                  <td style={{ padding: '8px' }}>
                    <ProgressBar
                      value={diversity}
                      max={1}
                      color={diversity > 0.6 ? colors.success : colors.warning}
                      label={`${(diversity * 100).toFixed(1)}%`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          padding: '20px',
          background: colors.bg,
          borderRadius: '12px',
          boxShadow: neuShadow,
          marginTop: '20px',
        }}
      >
        <h4 style={{ color: colors.text, marginBottom: '16px' }}>Category Representation</h4>
        <div style={{ fontSize: '12px' }}>
          {jobCategoryDist.map((cat, idx) => {
            const percent = ((cat.value / filteredPersonnel.length) * 100).toFixed(1);
            return (
              <div key={idx} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: colors.text, fontWeight: '500' }}>{cat.label}</span>
                  <span style={{ color: colors.primary, fontWeight: 'bold' }}>
                    {cat.value} ({percent}%)
                  </span>
                </div>
                <ProgressBar value={cat.value} max={filteredPersonnel.length} color={colors.primary} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ========================================================================
  // UTILITY FUNCTIONS
  // ========================================================================

  function monthlyTrend() {
    const months = {};
    const today = new Date();

    for (let i = 11; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      months[key] = 0;
    }

    personnel.forEach(p => {
      const hireDate = new Date(p.hireDate);
      if (hireDate <= today) {
        const monthCovered = hireDate;
        for (let i = 11; i >= 0; i--) {
          const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
          const key = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          if (monthCovered <= date) {
            months[key]++;
          }
        }
      }
    });

    return Object.entries(months).map(([label, value]) => ({ label, value }));
  }

  function calculateGrowthRate() {
    if (personnel.length === 0) return 0;
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    const newHires = personnel.filter(p => new Date(p.hireDate) > oneYearAgo).length;
    const baseCount = personnel.length - newHires;

    return baseCount > 0 ? ((newHires / baseCount) * 100) : 0;
  }

  function exportCSV() {
    let data = '';
    const displayData = filteredPersonnel;

    if (activeReport === 'headcount' && displayData.length > 0) {
      data = 'First Name,Last Name,Department,Job Title,Status,Hire Date\n';
      displayData.forEach(p => {
        data += `"${p.firstName}","${p.lastName}","${p.department}","${p.jobTitle}","${p.status}","${p.hireDate}"\n`;
      });
    } else if (activeReport === 'department' && selectedDepartment) {
      data = 'First Name,Last Name,Job Title,Status\n';
      displayData
        .filter(p => p.department === selectedDepartment)
        .forEach(p => {
          data += `"${p.firstName}","${p.lastName}","${p.jobTitle}","${p.status}"\n`;
        });
    } else {
      data = 'Report,Value\n';
      data += `Total Employees,${filteredPersonnel.length}\n`;
      data += `Turnover Rate,${turnoverMetrics.rate}%\n`;
      data += `Average Tenure,${avgTenure} years\n`;
    }

    const blob = new Blob([data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: colors.bg }}>
      {/* LEFT SIDEBAR - FILTERS */}
      <div
        style={{
          width: '240px',
          padding: '20px',
          background: colors.bg,
          borderRight: `1px solid ${colors.border}`,
          overflowY: 'auto',
        }}
      >
        <h3 style={{ color: colors.text, marginBottom: '16px', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Filter size={14} /> Filters
        </h3>

        {/* Date Range */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: colors.text, marginBottom: '6px' }}>
            Hire Date Range
          </label>
          <input
            type="date"
            value={dateRange.start}
            onChange={e => setDateRange({ ...dateRange, start: e.target.value })}
            style={{
              width: '100%',
              padding: '6px',
              marginBottom: '8px',
              fontSize: '11px',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              boxShadow: neuInset,
            }}
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={e => setDateRange({ ...dateRange, end: e.target.value })}
            style={{
              width: '100%',
              padding: '6px',
              fontSize: '11px',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              boxShadow: neuInset,
            }}
          />
        </div>

        {/* Department Filter */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: colors.text, marginBottom: '8px' }}>
            Departments
          </label>
          {departmentData.map(dept => (
            <label key={dept.label} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={departmentFilter.has(dept.label)}
                onChange={e => {
                  const newFilter = new Set(departmentFilter);
                  if (e.target.checked) {
                    newFilter.add(dept.label);
                  } else {
                    newFilter.delete(dept.label);
                  }
                  setDepartmentFilter(newFilter);
                }}
                style={{ marginRight: '6px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '11px', color: colors.text }}>{dept.label}</span>
            </label>
          ))}
        </div>

        {/* Status Filter */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: colors.text, marginBottom: '8px' }}>
            Status
          </label>
          {['Active', 'Inactive', 'Terminated'].map(status => (
            <label key={status} style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={statusFilter.has(status)}
                onChange={e => {
                  const newFilter = new Set(statusFilter);
                  if (e.target.checked) {
                    newFilter.add(status);
                  } else {
                    newFilter.delete(status);
                  }
                  setStatusFilter(newFilter);
                }}
                style={{ marginRight: '6px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '11px', color: colors.text }}>{status}</span>
            </label>
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={() => {
              setDateRange({ start: '', end: '' });
              setDepartmentFilter(new Set());
              setStatusFilter(new Set());
            }}
            style={{
              padding: '8px',
              background: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 'bold',
              boxShadow: neuShadow,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
            }}
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* TOP BAR - REPORT SELECTOR & EXPORT */}
        <div
          style={{
            background: colors.bg,
            borderBottom: `1px solid ${colors.border}`,
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Report Tabs */}
          <div style={{ display: 'flex', gap: '8px', overflow: 'auto', paddingBottom: '4px' }}>
            {[
              { id: 'headcount', label: 'Headcount Analysis', icon: Users },
              { id: 'department', label: 'Department Breakdown' },
              { id: 'tenure', label: 'Tenure Analysis', icon: Calendar },
              { id: 'turnover', label: 'Turnover Report' },
              { id: 'diversity', label: 'Diversity Overview' },
              { id: 'compensation', label: 'Compensation Overview' },
            ].map(report => (
              <button
                key={report.id}
                onClick={() => setActiveReport(report.id)}
                style={{
                  padding: '8px 14px',
                  background: activeReport === report.id ? colors.primary : colors.bg,
                  color: activeReport === report.id ? '#FFF' : colors.text,
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  boxShadow: activeReport === report.id ? neuShadow : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {report.label}
              </button>
            ))}
          </div>

          {/* Export Controls */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={exportCSV}
              style={{
                padding: '8px 12px',
                background: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: neuShadow,
              }}
            >
              <DownloadCloud size={14} /> CSV
            </button>
            <button
              onClick={() => alert('PDF export coming soon')}
              style={{
                padding: '8px 12px',
                background: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: neuShadow,
              }}
            >
              <FileText size={14} /> PDF
            </button>
            <button
              onClick={() => window.print()}
              style={{
                padding: '8px 12px',
                background: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                boxShadow: neuShadow,
              }}
            >
              <Printer size={14} /> Print
            </button>
          </div>
        </div>

        {/* REPORT CONTENT */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {renderReportContent()}
        </div>
      </div>
    </div>
  );
}
